import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useNavigate } from "react-router-dom";

// Import components
import LandingPage from './components/LandingPage';
import PropertyList from './components/PropertyList';
import PropertyForm from './components/PropertyForm';
import ProspectList from './components/ProspectList';
import Prospect from './components/Prospect';
import ProspectForm from './components/ProspectForm';
import AgentReport from './components/AgentReport';
import VendorReport from './components/VendorReport';

import { AuthProvider, useAuth } from "./AuthContext";
import { ReactComponent as Logo } from './assets/logo.svg';



import './App.css';

// Navigation component
const Navigation = () => {
  const { userId, logout } = useAuth();
  const profileImage = localStorage.getItem('profileImage');
  return (
    <nav className="bg-white shadow-sm">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <a href="/">
            <Logo className="h-12" alt="AgentSavvy Logo" />
          </a>

          {userId ? (
            <div className="flex items-center gap-6">
              <a href="/properties" className="text-gray-600 hover:text-blue-600">
                My Properties
              </a>
              <div className='flex items-center gap-1'>
                <img
                  src={profileImage}
                  alt=''
                  className="user-avatar"
                />
                <button
                  onClick={logout}
                  className="text-gray-600 hover:text-blue-600"
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <a
              href="/login"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Login
            </a>
          )}
        </div>
      </div>
    </nav>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { userId } = useAuth();

  if (!userId) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await login(email, password);
    if (ok) {
      const from = location.state?.from?.pathname || '/properties';
      navigate(from);
    } else {
      setError('Login failed. Please check your email and password.');
    }
  };

  const dismissError = () => {
    setError('');
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Agent Login</h1>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
            <button
              type="button"
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={dismissError}
            >
              <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 5.652a1 1 0 10-1.414-1.414L10 7.172 7.066 4.238a1 1 0 10-1.414 1.414L8.586 10l-2.934 2.934a1 1 0 101.414 1.414L10 12.828l2.934 2.934a1 1 0 001.414-1.414L11.414 10l2.934-2.934z" />
              </svg>
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              className="w-full p-2 border rounded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              className="w-full p-2 border rounded"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Login
          </button>
        </form>
      </div>
    </div>
  );
};
// Main App component
const App = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-gray-50">
          <Navigation />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/login"
              element={
                <Login />
              }
            />

            {/* Protected routes */}
            <Route
              path="/properties"
              element={
                <ProtectedRoute>
                  <PropertyList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/new"
              element={
                <ProtectedRoute>
                  <PropertyForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/:propertyId/Prospects"
              element={
                <ProtectedRoute>
                  <ProspectList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prospect/:prospectId"
              element={
                <ProtectedRoute>
                  <Prospect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/:propertyId/prospects/new"
              element={
                <ProtectedRoute>
                  <ProspectForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/:propertyId/inspections/:inspectionId"
              element={
                <ProtectedRoute>
                  <AgentReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties/:propertyId/inspections/:inspectionId/vendor"
              element={
                <ProtectedRoute>
                  <VendorReport />
                </ProtectedRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;